import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api/client';

/**
 * The public booking page's data layer.
 *
 * In the FRIDAY app these were react-query hooks. The marketing site does not
 * carry react-query and there is no reason to add it for four calls, so the
 * query and mutation shapes below are hand-rolled against plain `fetch`. The
 * surface is deliberately the same (isLoading / isError / isSuccess / refetch /
 * mutate / isPending) so the pages read the way they always did.
 *
 * Nobody calling these is logged in. No bearer is attached, and the public
 * endpoints never answer 401, so there is no unauthorised-redirect handler that
 * can fire out from under a half-filled booking form.
 */

/** Online or by phone. In-person bookings are not taken through this system. */
export type LocationMode = 'teams' | 'phone';

export interface BookingTypeMeta {
  slug: string;
  title: string;
  description: string | null;
  duration_mins: number;
  location_mode: LocationMode;
  timezone: string;
  /** The host's photo, if the booking type names one. Absent is normal and renders nothing. */
  host_photo_url?: string | null;
}

export interface AvailabilitySlot {
  /** 24-hour BRISBANE wall time, e.g. "09:30". Not what the page renders. */
  start: string;
  /**
   * The instant, as an epoch second. This is what the page renders from.
   *
   * "09:30" cannot be converted into anybody else's clock, because it does not say what
   * moment it means. Every visitor outside Brisbane reads a different number and some
   * read a different DAY, and both come from here.
   */
  starts_at: number;
  /** Signed, short-lived, and required to book this exact slot. */
  token: string;
}

export interface AvailabilityDay {
  /** YYYY-MM-DD in Brisbane. The page regroups these by the visitor's own date. */
  date: string;
  slots: AvailabilitySlot[];
}

export interface Availability {
  timezone: string;
  days: AvailabilityDay[];
}

export interface BookRequest {
  token: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  notes?: string;
  source?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  /** The visitor's real IANA zone, so their emails carry their own clock. */
  timezone?: string;
  /** Honeypot. Always empty for a real person. */
  website?: string;
}

export interface BookResponse {
  booking_id: string;
  starts_at_local: string;
  teams_join_url: string | null;
  cancel_url: string;
  /** This booking's own page. Where the visitor is sent once it lands. */
  reference_url: string;
}

/** One booking, as its own page reads it. Deliberately carries nothing internal. */
export interface BookingRef {
  slug: string;
  title: string;
  host_photo_url: string | null;
  status: 'confirmed' | 'cancelled';
  starts_at: number;
  starts_at_local: string;
  duration_mins: number;
  location_mode: LocationMode;
  teams_join_url: string | null;
  name: string | null;
  email: string;
  timezone: string;
}

/** The same booking after a move, plus whether anything actually changed. */
export interface RescheduleResponse extends BookingRef {
  /** False when the booking was already at that time, which is a retry landing twice. */
  moved: boolean;
}

export interface CancelResponse {
  cancelled: boolean;
  already_cancelled?: boolean;
}

export interface QueryResult<T> {
  data: T | undefined;
  error: ApiError | null;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  refetch: () => void;
}

interface MutateHandlers<T> {
  onSuccess?: (data: T) => void;
  onError?: (error: ApiError) => void;
}

export interface MutationResult<TInput, TOutput> {
  mutate: (input: TInput, handlers?: MutateHandlers<TOutput>) => void;
  data: TOutput | undefined;
  error: ApiError | null;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
}

/** Fetch on mount, refetch on demand, and drop answers that arrive after the key moved on. */
function useFetchQuery<T>(key: string, fetcher: () => Promise<T>, enabled: boolean): QueryResult<T> {
  const [state, setState] = useState<{ data?: T; error: ApiError | null; isLoading: boolean }>({
    error: null,
    isLoading: enabled,
  });
  const [nonce, setNonce] = useState(0);

  // The fetcher closes over the slug and is rebuilt every render. Holding it in
  // a ref keeps the effect keyed on the query key alone.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!enabled) {
      setState({ error: null, isLoading: false });
      return;
    }

    let live = true;
    // Keep whatever is already on screen while a refetch is in flight. The one
    // refetch that matters is the 409 path, and blanking the slot grid under
    // somebody who just lost a slot is a second thing going wrong, not a fix.
    setState((prev) => ({ data: prev.data, error: null, isLoading: true }));

    fetcherRef.current()
      .then((data) => {
        if (live) setState({ data, error: null, isLoading: false });
      })
      .catch((error: unknown) => {
        if (!live) return;
        setState({
          error: error instanceof ApiError ? error : new ApiError(0, 'Something went wrong at our end. Give it a moment and try again.'),
          isLoading: false,
        });
      });

    return () => { live = false; };
  }, [key, enabled, nonce]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  return {
    data: state.data,
    error: state.error,
    isLoading: state.isLoading,
    isError: state.error !== null,
    isSuccess: !state.isLoading && state.error === null && state.data !== undefined,
    refetch,
  };
}

function useMutation<TInput, TOutput>(
  fn: (input: TInput) => Promise<TOutput>,
  options?: { onError?: (error: ApiError) => void },
): MutationResult<TInput, TOutput> {
  const [state, setState] = useState<{ data?: TOutput; error: ApiError | null; isPending: boolean }>({
    error: null,
    isPending: false,
  });

  const fnRef = useRef(fn);
  fnRef.current = fn;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const mutate = useCallback((input: TInput, handlers?: MutateHandlers<TOutput>) => {
    setState({ error: null, isPending: true });
    fnRef.current(input)
      .then((data) => {
        setState({ data, error: null, isPending: false });
        handlers?.onSuccess?.(data);
      })
      .catch((raw: unknown) => {
        const error = raw instanceof ApiError
          ? raw
          : new ApiError(0, 'Something went wrong at our end. Give it a moment and try again.');
        setState({ error, isPending: false });
        optionsRef.current?.onError?.(error);
        handlers?.onError?.(error);
      });
  }, []);

  return {
    mutate,
    data: state.data,
    error: state.error,
    isPending: state.isPending,
    isSuccess: !state.isPending && state.error === null && state.data !== undefined,
    isError: state.error !== null,
  };
}

export function useBookingType(slug: string | undefined): QueryResult<BookingTypeMeta> {
  // An unknown slug is a typo in a printed link, not a transient failure. There
  // is no retry here on purpose: retrying just makes the visitor stare at a
  // spinner before the same answer.
  return useFetchQuery<BookingTypeMeta>(
    `type:${slug}`,
    () => api.get<BookingTypeMeta>(`/booking/public/${slug}`),
    Boolean(slug),
  );
}

export function useAvailability(slug: string | undefined, from: string, days = 14): QueryResult<Availability> {
  return useFetchQuery<Availability>(
    `availability:${slug}:${from}:${days}`,
    () => api.get<Availability>(`/booking/public/${slug}/availability?from=${from}&days=${days}`),
    Boolean(slug),
  );
}

/**
 * Book the selected slot.
 *
 * On a 409 the slot list is refetched before the error settles, so by the time
 * the visitor reads "that time was taken a moment ago" the grid behind the
 * message is already showing what is actually left.
 */
export function useBook(
  slug: string | undefined,
  onSlotConflict?: () => void,
): MutationResult<BookRequest, BookResponse> {
  const conflictRef = useRef(onSlotConflict);
  conflictRef.current = onSlotConflict;

  return useMutation<BookRequest, BookResponse>(
    (input) => api.post<BookResponse>(`/booking/public/${slug}/book`, input),
    {
      onError: (error) => {
        if (error.status === 409) conflictRef.current?.();
      },
    },
  );
}

export function useCancelBooking(token: string | undefined): MutationResult<void, CancelResponse> {
  return useMutation<void, CancelResponse>(
    () => api.post<CancelResponse>(`/booking/public/cancel/${token}`, {}),
  );
}

/**
 * Read one booking by its reference token.
 *
 * Unlike the cancel page this DOES read before the visitor does anything, and that is a
 * deliberate change of stance rather than an oversight. The cancel page renders nothing
 * before the POST because the only thing its link can do is destructive and it wanted no
 * part in turning a forwarded link into a record of who is meeting whom. A reference page
 * has to show the booking to be a page at all, so the protection moved to what the API
 * returns: attribution, the party id and the internal ids are all off the response.
 */
export function useBookingRef(token: string | undefined): QueryResult<BookingRef> {
  return useFetchQuery<BookingRef>(
    `ref:${token}`,
    () => api.get<BookingRef>(`/booking/public/ref/${token}`),
    Boolean(token),
  );
}

/**
 * Move a booking to another slot.
 *
 * On a 409 the slot list is refetched before the error settles, the same way the book
 * path does it, so the grid behind "that time was taken a moment ago" is already showing
 * what is left by the time it is read.
 */
export function useReschedule(
  token: string | undefined,
  onSlotConflict?: () => void,
): MutationResult<{ token: string }, RescheduleResponse> {
  const conflictRef = useRef(onSlotConflict);
  conflictRef.current = onSlotConflict;

  return useMutation<{ token: string }, RescheduleResponse>(
    (input) => api.post<RescheduleResponse>(`/booking/public/ref/${token}/reschedule`, input),
    {
      onError: (error) => {
        if (error.status === 409) conflictRef.current?.();
      },
    },
  );
}

/**
 * Today in Brisbane as YYYY-MM-DD. Brisbane is fixed UTC+10, so this is arithmetic.
 *
 * Still Brisbane and deliberately so, even though the page now renders in the visitor's
 * zone. This is the `from` parameter on the availability request, and the API reads it
 * as a Brisbane date key against Brisbane working hours. Sending the visitor's local
 * date would ask for the wrong window whenever the two disagree.
 *
 * Date labels and slot times used to be formatted here as well, off the same fixed
 * offset. Both moved to lib/timezones.ts and both go through Intl now, because an
 * offset is only ever right for Queensland.
 */
export function brisbaneToday(now: Date = new Date()): string {
  return new Date(now.getTime() + 10 * 3600 * 1000).toISOString().slice(0, 10);
}
